import { Component, Input } from '@angular/core';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { ExceptionResolution, OrderItem } from '@usf/ngrx-order';
import { ProductPropertiesEnum } from '@usf/product-types';
import { take } from 'rxjs/operators';
import { ResolutionType } from '@order/pages/order-exceptions/constants/resolution-type.enum';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import { UsfProductCardModeEnum } from 'src/app/shared/constants/usf-product-card-mode.enum';
import { ApiUpdatable } from 'src/app/shared/models/api-updatable';
import { QuantityUpdate } from 'src/app/shared/models/quantity-update';
import { ProductService } from 'src/app/shared/services/product/product.service';
import { Product } from '../../../shared/models/product.model';
import { OrderService } from '@order/services/order.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-product-exception-popover',
  templateUrl: './product-exception-popover.component.html',
  styleUrls: ['./product-exception-popover.component.scss'],
})
export class ProductExceptionPopoverComponent
  implements ViewWillEnter, ViewWillLeave
{
  @Input() product: Product;
  @Input() replacementProduct: Product;
  @Input() redirectLocation: string;
  @Input() mode: UsfProductCardModeEnum;
  @Input() exceptionId: string;
  @Input() quantityEach: ApiUpdatable<number>;
  @Input() quantityUnit: ApiUpdatable<number>;
  isDisabledConfirm = true;
  productPropertiesEnum = ProductPropertiesEnum;

  exceptionResolution?: ExceptionResolution;
  featureFlagSub$: Subscription;
  isPartialExceptionsFlagEnabled = false;

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    readonly orderService: OrderService,
    private productService: ProductService,
    private modalController: ModalController,
    private orderExceptionService: OrderExceptionService,
  ) {}

  confirmResolveException() {
    const subOrderItem: OrderItem = {
      sequence: 1,
      eachesOrdered: this.quantityEach,
      unitsOrdered: this.quantityUnit,
      productNumber: this.replacementProduct.productNumber,
      purchasedFromVendor: this.replacementProduct.inventory.purchaseFromVendor,
      vendorLeadTime: this.replacementProduct.inventory.vendorLeadTime,
      specialVendor: this.replacementProduct.summary.properties?.has(
        ProductPropertiesEnum.specialVendor,
      ),
    } as OrderItem;
    this.orderExceptionService.resolveException(
      this.exceptionResolution,
      ResolutionType.SUBSTITUTE,
      subOrderItem,
      this.isPartialExceptionsFlagEnabled,
    );
    this.modalController.dismiss({ success: true });
  }

  async dismiss() {
    await this.modalController.dismiss({ success: false });
  }

  ionViewWillEnter(): void {
    this.productService.loadProducts([
      this.product.productNumber,
      this.replacementProduct.productNumber,
    ]);
    this.orderExceptionService
      .getExceptionResolutionWithId(this.exceptionId)
      .pipe(take(1))
      .subscribe(payload => {
        this.exceptionResolution = payload;
        this.quantityUnit.currentValue =
          payload.orderItem?.unitsOrdered?.currentValue;
        this.quantityUnit.previousValue =
          payload.orderItem?.unitsOrdered?.currentValue;
        if (this.replacementProduct?.summary?.breakable) {
          this.quantityEach.currentValue =
            payload.orderItem?.eachesOrdered?.currentValue;
          this.quantityEach.previousValue =
            payload.orderItem?.eachesOrdered?.currentValue;
        }
        this.isDisabledConfirm = false;
      });
    this.featureFlagSub$ = this.panAppState
      .feature$([FEATURES.split_dynamic_order_partial_exceptions.name])
      ?.subscribe(isPartialExceptionsEnabled => {
        this.isPartialExceptionsFlagEnabled = isPartialExceptionsEnabled;
      });
  }

  ionViewWillLeave(): void {
    this.featureFlagSub$?.unsubscribe();
  }

  updateQuantity(event: QuantityUpdate) {
    if (event.isEaches) {
      this.quantityEach.previousValue = this.quantityEach.currentValue;
      this.quantityEach.currentValue = event.quantity;
    } else {
      this.quantityUnit.previousValue = this.quantityUnit.currentValue;
      this.quantityUnit.currentValue = event.quantity;
    }

    const replaceProduct = !!this.replacementProduct?.alternative?.product
      ? this.replacementProduct?.alternative?.product
      : this.replacementProduct;
    if (!!replaceProduct) {
      if (replaceProduct?.summary?.breakable)
        this.isDisabledConfirm =
          !this.quantityEach?.currentValue && !this.quantityUnit?.currentValue;
      else this.isDisabledConfirm = !this.quantityUnit?.currentValue;
    }
  }
}
